import { useState } from 'react';

// ─── Styles ───────────────────────────────────────────────────────────────────

const paginationStyle = `
  .pg-root {
    display: flex;
    flex-direction: column;
    gap: 24px;
    padding: 32px;
    background: #f7f7f5;
    min-height: 100vh;
  }
 
  .pg-bar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    background: #ffffff;
    padding: 10px 16px;
  }
 
  .pg-label {
    font-size: 12px;
    color: #888;
    letter-spacing: 0.01em;
    white-space: nowrap;
  }
 
  .pg-label span {
    color: #222;
    font-weight: 500;
  }
 
  .pg-controls {
    display: flex;
    align-items: center;
    gap: 2px;
  }
 
  .pg-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    min-width: 32px;
    height: 32px;
    padding: 0 6px;
    border-radius: 7px;
    border: none;
    background: transparent;
    font-size: 13px;
    font-weight: 500;
    color: #555;
    cursor: pointer;
    transition: background 0.13s, color 0.13s;
    user-select: none;
  }
 
  .pg-btn:hover:not(:disabled):not(.pg-active) {
    background: #f0efec;
    color: #111;
  }
 
  .pg-btn:disabled {
    opacity: 0.35;
    cursor: not-allowed;
  }
 
  .pg-active {
    background: #3762FB;
    color: #fff !important;
    border-radius: 7px;
  }
 
  .pg-ellipsis {
    display: flex;
    align-items: center;
    justify-content: center;
    min-width: 32px;
    height: 32px;
    font-size: 13px;
    color: #aaa;
    letter-spacing: 0.1em;
    pointer-events: none;
  }
 
  .pg-arrow {
    color: #555;
    font-size: 15px;
  }
 
  .pg-debug {
    font-size: 11px;
    color: #999;
    background: #fff;
    border: 1px dashed #e0e0dc;
    border-radius: 8px;
    padding: 10px 14px;
    display: flex;
    gap: 24px;
  }
 
  .pg-debug strong {
    color: #444;
  }
`;

// ─── Types ────────────────────────────────────────────────────────────────────

export interface PaginationProps {
  /** Total number of records */
  total: number;
  /** Records per page */
  perPage?: number;
  /** Entity label shown in the summary */
  label?: string;
  /** Currently active page (1-indexed) — controlled by parent */
  page: number;
  /** Called when the user selects a new page */
  onPageChange: (page: number) => void;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

type PageItem = number | '...';

function getPageNumbers(current: number, total: number): PageItem[] {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);

  // Near the start — show first 5 pages, then ellipsis, then last 2
  if (current <= 4) {
    return [1, 2, 3, 4, 5, '...', total - 1, total];
  }
  // Near the end — show first 2, then ellipsis, then last 5
  if (current >= total - 3) {
    return [1, 2, '...', total - 4, total - 3, total - 2, total - 1, total];
  }
  // Middle — show first, ellipsis, current-1, current, current+1, ellipsis, last
  return [1, '...', current - 1, current, current + 1, '...', total];
}

// ─── AppPagination Component ─────────────────────────────────────────────────────

export function AppPagination({
  total,
  perPage = 20,
  label = 'Items',
  page,
  onPageChange,
}: PaginationProps) {
  const totalPages = Math.ceil(total / perPage);
  const from = (page - 1) * perPage + 1;
  const to = Math.min(page * perPage, total);
  const pages = getPageNumbers(page, totalPages);

  return (
    <>
      <style>{paginationStyle}</style>
      <div className="pg-bar">
        <span className="pg-label">
          Showing{' '}
          <span>
            {from} to {to}
          </span>{' '}
          from <span>{total.toLocaleString()}</span> {label}
        </span>

        <div className="pg-controls">
          <button
            className="pg-btn pg-arrow"
            onClick={() => onPageChange(Math.max(1, page - 1))}
            disabled={page === 1}
            aria-label="Previous page">
            ‹
          </button>

          {pages.map((p, i) =>
            p === '...' ? (
              <span key={`ellipsis-${i}`} className="pg-ellipsis">
                ···
              </span>
            ) : (
              <button
                key={p}
                className={`pg-btn${page === p ? ' pg-active' : ''}`}
                onClick={() => onPageChange(p)}
                aria-label={`Page ${p}`}
                aria-current={page === p ? 'page' : undefined}>
                {p}
              </button>
            )
          )}

          <button
            className="pg-btn pg-arrow"
            onClick={() => onPageChange(Math.min(totalPages, page + 1))}
            disabled={page === totalPages}
            aria-label="Next page">
            ›
          </button>
        </div>
      </div>
    </>
  );
}
